
import { useState, useEffect } from 'react';
import { fetchNVDBData, fetchSwedishRestAreas, fetchFinnishRestAreas, fetchOSMRestAreas, fetchDanishSheltersAndCamping, calculateRestAreaScore } from '../services/nvdbService';
import { CampingSpot, Coordinates, MapBounds } from '../types';
import { detectViewportCountry } from '../utils/countryDetection';

export const useRestAreas = (
  active: boolean,
  zoom: number,
  currentMapCenter: Coordinates,
  currentViewBounds: MapBounds | null,
  onNewSpots: (spots: CampingSpot[]) => void,
  swedenEnabled?: boolean,
  denmarkEnabled?: boolean,
  finlandEnabled?: boolean,
) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!active || zoom < 11) {
      setIsTruncated(false);
      setIsLoading(false);
      return;
    }

    const activeBounds = currentViewBounds || {
      latMin: currentMapCenter.latitude - 0.05,
      lonMin: currentMapCenter.longitude - 0.1,
      latMax: currentMapCenter.latitude + 0.05,
      lonMax: currentMapCenter.longitude + 0.1
    };

    const fetchRestAreas = async () => {
      setIsLoading(true);
      try {
        // Country-aware fetching: Norwegian NVDB for Norway, Trafikverket for Sweden,
        // Digiroad for Finland (levähdysalue/pysäköintialue — lovlige oppstillingsplasser).
        const country = detectViewportCountry(activeBounds);
        const isNorway = country === 'NO' || country === null;
        const isSweden = country === 'SE' && swedenEnabled;
        const isDenmark = country === 'DK' && denmarkEnabled;
        const isFinland = country === 'FI' && finlandEnabled;
        const isBorder = country === null;

        const promises = [];
        if (isNorway || isBorder) promises.push(fetchNVDBData(activeBounds));
        if (isSweden || (isBorder && swedenEnabled)) promises.push(fetchSwedishRestAreas(activeBounds));
        if (isFinland) promises.push(fetchFinnishRestAreas(activeBounds));
        if (isDenmark) {
          promises.push(fetchOSMRestAreas(activeBounds));
          promises.push(fetchDanishSheltersAndCamping(activeBounds));
        }

        const results = await Promise.all(promises);

        // Merge results
        let allObjects = results.flatMap(r => r.objects);
        const truncated = results.some(r => r.isTruncated);
        setIsTruncated(truncated);

        if (allObjects.length > 0) {
          setError(false);
          const restAreaSpots: CampingSpot[] = allObjects.map(obj => {
            const isSnuplass = obj.typeId === 40;
            // Snuplass skal ikke ha score i det hele tatt
            const restScore = isSnuplass ? 0 : calculateRestAreaScore(obj.rasteplassInfo);

            // Logikk for natt-vennlighet
            const canSleep = !isSnuplass && obj.rasteplassInfo?.toalett === true && obj.rasteplassInfo?.vinterstengt === false;

            return {
              id: `nvdb-${obj.id}`,
              source: 'nvdb',
              externalId: String(obj.id),
              importVersion: 'nvdb-v4',
              lastSyncedAt: new Date().toISOString(),
              name: obj.name || (isSnuplass ? "Snuplass" : "Rasteplass"),
              spotType: 'rasteplass',
              sourceLevel: 1,
              location: obj.location,
              accessRoute: obj.geometryPoints || undefined,
              nearestHouse: 999,
              description: isSnuplass ? "Verifisert snuplass eller lomme (SVV)." : `Verifisert ${obj.rasteplassInfo?.type?.toLowerCase() || 'rasteplass'} (SVV).`,
              score: restScore,
              suitabilityColor: '#2563eb', // Alltid blått for offentlige rasteplasser
              features: [isSnuplass ? "SNUPLASS" : "RASTEPLASS", "NVDB"],
              reasoning: "Offentlig verifisert rasteplass.",
              trafficRisk: "Normal",
              buildingProximity: "N/A",
              highway: "Veg",
              surfaceType: 'Asfalt',
              isPublicAccess: true,
              isSleepFriendly: canSleep,
              rasteplassMetadata: obj.rasteplassInfo,
              legal: {
                isUtmark: true,
                inProtectedArea: false,
                hasBarrier: false,
                distToFarmland: 999,
                nearMajorRoad: true
              }
            };
          });
          onNewSpots(restAreaSpots);
        }
      } catch (e) {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    // Increased debounce to 1200ms
    const timer = setTimeout(fetchRestAreas, 1200);
    return () => clearTimeout(timer);
  }, [currentViewBounds, active, zoom, swedenEnabled, denmarkEnabled, finlandEnabled]);

  return { isTruncated, error, isLoading };
};